import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useUploadProgress } from "@/contexts/UploadProgressContext";
import UploadPage from "@/pages/Upload";

/** /upload-origin exists/new review (not Add Study mismatch). */
function isStandaloneUploadReview(file) {
  if (!file?.patient_mismatch || file.status !== "extracted") return false;
  if (file.origin === "add_study") return false;
  if (
    file.mismatch_kind === "add_study_exists"
    || file.mismatch_kind === "add_study_new"
    || file.mismatch_kind === "locked"
  ) {
    return false;
  }
  if (file.mismatch_kind === "exists" || file.mismatch_kind === "new") return true;
  return file.origin === "upload";
}

const isStandaloneUploadFile = (file) => {
  if (!file) return false;
  if (file.origin === "add_study" || file.queue === "add_study" || file.locked_patient_uhid) {
    return false;
  }
  if (
    file.mismatch_kind === "add_study_exists"
    || file.mismatch_kind === "add_study_new"
    || file.mismatch_kind === "locked"
  ) {
    return false;
  }
  return true;
};

/**
 * Hidden /upload processor for when the user leaves the upload page after dropping
 * files. Keeps extract classification, auto-save, and exists/new review alive
 * on any route (Google Drive–style background upload).
 */
export default function ExistsMismatchReviewHost() {
  const location = useLocation();
  const { pendingMismatchFileId, files } = useUploadProgress();

  const onUploadPage = location.pathname.startsWith("/upload");

  const shouldMount = useMemo(() => {
    if (onUploadPage) return false;

    const hasActiveStandaloneWork = files.some((f) => (
      isStandaloneUploadFile(f) && f.status !== "completed"
    ));
    if (hasActiveStandaloneWork) return true;

    if (!pendingMismatchFileId) return false;
    const pendingFile = files.find((f) => f.id === pendingMismatchFileId);
    return isStandaloneUploadReview(pendingFile);
  }, [files, onUploadPage, pendingMismatchFileId]);

  if (!shouldMount) return null;

  return (
    <div
      className="pointer-events-none fixed left-0 top-0 z-[-1] h-0 w-0 overflow-hidden opacity-0"
      data-testid="exists-mismatch-review-host"
    >
      <UploadPage backgroundProcess />
    </div>
  );
}
